/**
 * A search field (see /styleguide), its icon inside on the left. It searches
 * on Enter (the keyboard's Search key), with no button beside it taking room:
 * a Search button shows under it only while what's typed hasn't been searched
 * for. Its × (or Escape) clears it, and the search.
 */
import * as preact from "preact";

interface Props {
  /** What's typed */
  value: string;
  /** What's searched for */
  query: string;
  placeholder: string;
  onInput: (value: string) => void;
  /** To search for `query` (trimmed), or clear the search ("") */
  onSearch: (query: string) => void;
  /** First, to handle a key itself (preventing the default: no search) */
  onKeyDown?: (event: KeyboardEvent) => void;
  inputRef?: preact.Ref<HTMLInputElement>;
}

export default class SearchField extends preact.Component<Props> {
  private clear = () => {
    this.props.onInput("");
    this.props.onSearch("");
  };

  private keyDown = (event: KeyboardEvent) => {
    this.props.onKeyDown?.(event);
    // Not as an IME's Enter picks what's composed
    if (event.defaultPrevented || event.isComposing) return;
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      // The input's own value, which may be ahead of a render
      this.props.onSearch(
        (event.currentTarget as HTMLInputElement).value.trim()
      );
    } else if (event.key === "Escape") {
      this.clear();
    }
  };

  render() {
    const { value, query, placeholder, inputRef } = this.props;
    const typed = value.trim();
    return (
      <span class="searchInputContainer">
        <input
          class="searchInput"
          type="text"
          enterKeyHint="search"
          ref={inputRef}
          placeholder={placeholder}
          value={value}
          onInput={(event) => this.props.onInput(event.currentTarget.value)}
          onKeyDown={this.keyDown}
        />
        <i class="icon-search searchIcon"></i>
        {value !== "" ? (
          <i
            class="icon-remove clearSearchButton"
            role="button"
            aria-label="Clear"
            onClick={this.clear}
          ></i>
        ) : null}
        {typed !== "" && typed !== query ? (
          <button
            class="searchButton"
            onClick={() => this.props.onSearch(typed)}
          >
            <i class="icon-search"></i> &nbsp; Search
          </button>
        ) : null}
      </span>
    );
  }
}
